import { useSetAtom } from "jotai";
import { ReactNode, useCallback } from "react";
import { Button } from "@zapier/design-system-beta";
import {
  emitEventOnInterfacesPlanUpgradeAtom,
  emitEventOnTablesPlanUpgradeAtom,
  emitEventOnChatbotsPlanUpgradeAtom,
} from "@zapier/canvas-state";
import { useTabFocus } from "../../hooks/useTabFocus";
import { getNewProductUpgradeHref } from "../../utils/getNewProductUpgradeHref";
import { BannerWrapper } from "../BannerWrapper";

type BannerLocation = "AI banner" | "Template banner";

type Props = {
  title: string;
  description: string;
  tablesPlanType?: "premium" | "advanced";
  interfacesPlanType?: "premium" | "advanced";
  chatbotsPlanType?: "premium" | "advanced";
  location: BannerLocation;
  children?: ReactNode;
};

/** IMPORTANT! This banner is s being used in the following locations:
 *  - AI Copilot
 *  - template widget
 *  - template page (ex: app/canvas/template/lead-capture)
 *
 *  Be conscientious that changes made here will affect all three.
 *
 *  If you want this banner to show up locally, return an object in upgradeBannerPropsAsyncAtom
 * */
export function UpgradeBanner({
  title,
  description,
  tablesPlanType,
  interfacesPlanType,
  chatbotsPlanType,
  location,
  children,
}: Props) {
  useTabFocus();

  const emitEventOnTablesPlanUpgrade = useSetAtom(
    emitEventOnTablesPlanUpgradeAtom,
  );
  const emitEventOnInterfacesPlanUpgrade = useSetAtom(
    emitEventOnInterfacesPlanUpgradeAtom,
  );
  const emitEventOnChatbotsPlanUpgrade = useSetAtom(
    emitEventOnChatbotsPlanUpgradeAtom,
  );

  const handleEmitEvents = useCallback(() => {
    const TABLES_EVENT_NAME = `Upgrade Tables - ${location}`;
    const INTERFACES_EVENT_NAME = `Upgrade Interfaces - ${location}`;
    const CHATBOTS_EVENT_NAME = `Upgrade Chatbots - ${location}`;

    if (tablesPlanType) {
      emitEventOnTablesPlanUpgrade(TABLES_EVENT_NAME);
    }

    if (interfacesPlanType) {
      emitEventOnInterfacesPlanUpgrade(INTERFACES_EVENT_NAME);
    }

    if (chatbotsPlanType) {
      emitEventOnChatbotsPlanUpgrade(CHATBOTS_EVENT_NAME);
    }
  }, [
    emitEventOnInterfacesPlanUpgrade,
    emitEventOnTablesPlanUpgrade,
    emitEventOnChatbotsPlanUpgrade,
    interfacesPlanType,
    tablesPlanType,
    chatbotsPlanType,
    location,
  ]);

  const handleUpgrade = useCallback(() => {
    handleEmitEvents();
    const href = getNewProductUpgradeHref({
      tablesPlanType,
      interfacesPlanType,
      chatbotsPlanType,
    });
    window.open(href, "_blank");
  }, [interfacesPlanType, handleEmitEvents, tablesPlanType, chatbotsPlanType]);

  /* AI Copilot: all 3 buttons are rendered (start over, modify prompt, upgrade)
   * Template widget: 2 buttons are rendered (start blank and upgrade)
   * Template page: only upgrade button is rendered
   * */
  return (
    <BannerWrapper title={title} description={description}>
      {children}
      <Button onPress={handleUpgrade}>Upgrade</Button>
    </BannerWrapper>
  );
}
